const { getElement, getElements, formatMoney } = window.BAO.Utils

/**
 * @typedef {Object} DiscountTier
 * @property {number} quantity - Minimum quantity to qualify for this tier
 * @property {number} percentage - Discount percentage applied at this tier
 */

/**
 * @typedef {Object} Product
 * @property {number} id - Unique identifier for the product
 * @property {number[]} variant_ids - Array of variant IDs for the product
 */

/**
 * @typedef {Object} ProductGroup
 * @property {string} id - Unique identifier for the product group
 * @property {Product[]} products - Array of products in the group
 */

/**
 * @typedef {Object} DiscountConfig
 * @property {DiscountTier[]} discount_tiers - Tiered discounts applied by quantity
 * @property {ProductGroup[]} product_groups - Groups of related products
 */

/** @typedef {string} ProductId */
/** @typedef {string} VariantId */

const PRODUCT_SET_EVENTS = {
  UPDATED: 'PRODUCT_SET:UPDATED',
  OPTIONS_RENDERED: 'PRODUCT_SET:OPTIONS_RENDERED',
}

class ProductSet extends window.BAO.CustomElement() {
  static #url = new URL(window.location.href)
  static htmlOptionsCache = new Map()

  /**
   * @param {ProductId} productId
   * @param {VariantId} variantId
   */
  static add (productId, variantId) {
    ProductSet.#url.searchParams.set(`product-${productId}`, variantId)

    ProductSet.updateUrl()
  }

  /**
   * @param {ProductId} productId
   */
  static delete (productId) {
    ProductSet.#url.searchParams.delete(`product-${productId}`)

    ProductSet.updateUrl()
  }

  /**
   * @param {ProductId} productId
   */
  static has (productId) {
    return ProductSet.#url.searchParams.has(`product-${productId}`)
  }

  /**
   * @param {ProductId} productId
   * @returns {VariantId|null}
   */
  static get (productId) {
    return ProductSet.#url.searchParams.get(`product-${productId}`)
  }

  /**
   * @returns {string[]}
   */
  static getAllKeys () {
    return [...ProductSet.#url.searchParams.keys()].filter((key) =>
      key.startsWith('product-'),
    )
  }

  /**
   * @returns {number}
   */
  static get size () {
    return [...ProductSet.#url.searchParams.keys()].filter((key) =>
      key.startsWith('product-'),
    ).length
  }

  static clear () {
    ProductSet.#url.searchParams.delete('group')

    const productKeys = [...ProductSet.#url.searchParams.keys()].filter((key) =>
      key.startsWith('product-'),
    )
    productKeys.forEach((key) => ProductSet.#url.searchParams.delete(key))

    ProductSet.updateUrl()
  }

  /**
   * @returns {string}
   */
  static getGroup () {
    return ProductSet.#url.searchParams.get('group') ?? 'product-group-1'
  }

  /**
   * @param {string} groupId
   */
  static setGroup (groupId) {
    if (ProductSet.getGroup() === groupId) return

    ProductSet.clear()

    ProductSet.#url.searchParams.set('group', groupId)

    ProductSet.updateUrl()
  }

  static updateUrl () {
    window.history.replaceState(null, '', ProductSet.#url.toString())
    window.BAO.dispatchEvent(PRODUCT_SET_EVENTS.UPDATED)
  }

  static get requiredElements () {
    return [
      'empty-text',
      'summary-product',
      'submit',
      'current-saving',
      'incentive-saving',
      'group',
      'actions',
      'data',
    ]
  }

  setupEls () {
    /** @type {HTMLElement[]} */
    this.emptyTextEls = this.els?.['empty-text']?.elements ?? []
    /** @type {HTMLElement[]} */
    this.summaryProductEls = this.els?.['summary-product']?.elements ?? []
    /** @type {HTMLElement[]} */
    this.currentSavingEls = this.els?.['current-saving']?.elements ?? []
    /** @type {HTMLElement[]} */
    this.incentiveSavingEls = this.els?.['incentive-saving']?.elements ?? []
    /** @type {HTMLButtonElement[]} */
    this.submitEls = this.els?.submit?.elements ?? []
    /** @type {HTMLElement[]} */
    this.groupEls = this.els?.group?.elements ?? []
    /** @type {HTMLScriptElement} */
    this.dataEl = this.els?.data?.element
    /** @type {ProductSetSummarySticky} */
    this.summaryStickyEl = getElement('product-set-summary-sticky')
  }

  setupListeners () {
    super.setupListeners()
    this.setupEls()

    this.listeners.add(
      window.BAO.eventBus,
      PRODUCT_SET_EVENTS.UPDATED,
      this.handleProductSetUpdate.bind(this),
    )

    this.listeners.add(
      window.BAO.eventBus,
      PRODUCT_SET_EVENTS.OPTIONS_RENDERED,
      this.handleProductSetUpdate.bind(this),
    )
  }

  connectedCallback () {
    super.connectedCallback()

    this.setData()
    this.validateUrlParams()
    this.handleProductSetUpdate()
  }

  setData () {
    this.data = this.dataEl ? JSON.parse(this.dataEl.textContent) : null
  }

  validateUrlParams () {
    const group = ProductSet.getGroup()
    const productGroups = this.data?.product_groups ?? []
    const productIds =
      productGroups
        .find((productGroup) => productGroup.id === group)
        ?.products?.map((product) => product.id) ?? []

    if (!group || !productIds.length) {
      ProductSet.clear()

      return
    }

    ProductSet.getAllKeys()
      .map((key) => key.replace('product-', ''))
      .map((id) => Number(id))
      .forEach((productIdInUrl) => {
        if (!productIds.includes(productIdInUrl)) {
          ProductSet.delete(productIdInUrl)
        }

        const variantIds =
          productGroups
            .find((productGroup) => productGroup.id === group)
            ?.products?.find((product) => product.id === productIdInUrl)
            ?.variant_ids ?? []

        if (!variantIds.includes(Number(ProductSet.get(productIdInUrl)))) {
          ProductSet.delete(productIdInUrl)
        }
      })
  }

  handleSelectChange (event) {
    /** @type {HTMLSelectElement} */
    const selectEl = event.currentTarget
    const productId = selectEl.dataset.productId
    const variantId = selectEl.value

    if (variantId) {
      ProductSet.add(productId, variantId)
    } else {
      ProductSet.delete(productId)
    }
  }

  handleProductSetUpdate () {
    this.toggleEmptyVisibility()
    this.toggleSummaryProductVisibility()
    this.updateSavings()
    this.updateSubmitButton()
  }

  toggleEmptyVisibility () {
    this.emptyTextEls.forEach((el) => {
      el.setAttribute('aria-hidden', ProductSet.size > 0)
    })
  }

  toggleSummaryProductVisibility () {
    this.summaryProductEls.forEach((el) => {
      el.setAttribute('aria-hidden', !ProductSet.has(el.dataset.productId))
    })
  }

  updateSavings () {
    const maxDiscountTiers =
      this.data?.product_groups?.find(
        (productGroup) => productGroup.id === ProductSet.getGroup(),
      )?.products?.length ?? 1
    const discountTiers = (this.data?.discount_tiers ?? [])
      .slice(0, maxDiscountTiers - 1)
      .sort((a, b) => a.quantity - b.quantity)

    const groupEl = this.groupEls.find((el) => el.id === ProductSet.getGroup())
    const actionsEl =
      groupEl && this.els?.actions?.selector
        ? getElements(this.els.actions.selector, { context: groupEl })
        : []
    const prices = actionsEl
      .filter((el) => ProductSet.has(el.dataset.productId))
      .map((el) => Number(el.dataset.price))

    const currentTier = discountTiers.find(
      (discountTier) => discountTier.quantity === ProductSet.size,
    )
    const nextTier = discountTiers.find(
      (discountTier) => discountTier.quantity > ProductSet.size,
    )

    const subTotalPrice = prices.reduce((acc, price) => acc + price, 0)
    const discountedPrice = this.getDiscountedPrice(prices, currentTier)

    const formattedSubTotal = formatMoney(subTotalPrice).replace('.00', '')
    const formattedDiscountedPrice = formatMoney(discountedPrice).replace(
      '.00',
      '',
    )

    this.setCurrentSaving(currentTier, nextTier, formattedSubTotal)
    this.setIncentiveSaving(
      currentTier,
      nextTier,
      prices,
      formattedSubTotal,
      formattedDiscountedPrice,
    )
    this.updateStickySummaryTotals(
      subTotalPrice,
      discountedPrice,
      formattedSubTotal,
      formattedDiscountedPrice,
    )
  }

  /**
   * @param {DiscountTier|null} currentTier
   * @param {DiscountTier|null} nextTier
   * @param {string} formattedSubTotal
   */
  setCurrentSaving (currentTier, nextTier, formattedSubTotal) {
    if (!currentTier) {
      this.currentSavingEls.forEach((el) => {
        el.setAttribute('aria-hidden', true)
      })

      return
    }

    this.currentSavingEls.forEach((el) => {
      const text = el.dataset.text
        .replace('[[ percent ]]', currentTier.percentage)
        .replace('[[ count ]]', currentTier.quantity)
      const textEl = getElement('[data-text-el]', { context: el })
      const valueEl = getElement('[data-value-el]', { context: el })

      if (textEl) textEl.textContent = nextTier ? text : ''
      if (valueEl) valueEl.textContent = formattedSubTotal

      el.setAttribute('aria-hidden', false)
    })
  }

  /**
   * @param {DiscountTier|null} currentTier
   * @param {DiscountTier|null} nextTier
   * @param {number[]} prices
   * @param {string} formattedSubTotal
   * @param {string} formattedDiscountedPrice
   */
  setIncentiveSaving (
    currentTier,
    nextTier,
    prices,
    formattedSubTotal,
    formattedDiscountedPrice,
  ) {
    if (nextTier) {
      this.setIncentiveSavingForNextTier(
        currentTier,
        nextTier,
        prices,
        formattedSubTotal,
        formattedDiscountedPrice,
      )

      return
    }

    if (currentTier) {
      this.setIncentiveSavingForCurrentTier(
        currentTier,
        formattedDiscountedPrice,
      )

      return
    }

    this.setIncentiveSavingForNoTiers(formattedDiscountedPrice)
  }

  /**
   * @param {DiscountTier|null} currentTier
   * @param {DiscountTier|null} nextTier
   * @param {number[]} prices
   * @param {string} formattedSubTotal
   * @param {string} formattedDiscountedPrice
   */
  setIncentiveSavingForNextTier (
    currentTier,
    nextTier,
    prices,
    formattedSubTotal,
    formattedDiscountedPrice,
  ) {
    const productsNeeded = nextTier.quantity - ProductSet.size

    this.incentiveSavingEls.forEach((el) => {
      const text = this.getIncentiveText(el, productsNeeded)
        .replace('[[ percent ]]', nextTier.percentage)
        .replace('[[ count ]]', productsNeeded)
      const textEl = getElement('[data-text-el]', { context: el })
      const valueEl = getElement('[data-value-el]', { context: el })

      if (textEl) textEl.textContent = text

      if (valueEl) {
        if (prices.length === 0) {
          valueEl.textContent = ''
        } else if (currentTier) {
          valueEl.textContent = formattedDiscountedPrice
        } else {
          valueEl.textContent = formattedSubTotal
        }
      }

      el.setAttribute('aria-hidden', false)
    })
  }

  /**
   * @param {DiscountTier|null} currentTier
   * @param {string} formattedDiscountedPrice
   */
  setIncentiveSavingForCurrentTier (currentTier, formattedDiscountedPrice) {
    this.incentiveSavingEls.forEach((el) => {
      const text = el.dataset.textFinalSaving
        .replace('[[ percent ]]', currentTier.percentage)
        .replace('[[ count ]]', currentTier.quantity)
      const textEl = getElement('[data-text-el]', { context: el })
      const valueEl = getElement('[data-value-el]', { context: el })

      if (textEl) textEl.textContent = text
      if (valueEl) valueEl.textContent = formattedDiscountedPrice

      el.setAttribute('aria-hidden', false)
    })
  }

  setIncentiveSavingForNoTiers (formattedDiscountedPrice) {
    this.incentiveSavingEls.forEach((el) => {
      const textEl = getElement('[data-text-el]', { context: el })
      const valueEl = getElement('[data-value-el]', { context: el })

      if (textEl) textEl.textContent = ''
      if (valueEl) valueEl.textContent = formattedDiscountedPrice

      el.setAttribute('aria-hidden', false)
    })
  }

  /**
   * @param {HTMLElement} el
   * @param {number} productsNeeded
   * @returns {string}
   */
  getIncentiveText (el, productsNeeded) {
    if (ProductSet.size === 0 && productsNeeded === 1) {
      return el.dataset.textFirstSingular
    }
    if (ProductSet.size === 0 && productsNeeded > 1) {
      return el.dataset.textFirstPlural
    }
    if (ProductSet.size > 0 && productsNeeded === 1) {
      return el.dataset.textAnotherSingular
    }
    if (ProductSet.size > 0 && productsNeeded > 1) {
      return el.dataset.textAnotherPlural
    }

    return ''
  }

  /**
   * @param {number[]} prices
   * @param {DiscountTier|null} discountTier
   * @returns {number}
   */
  getDiscountedPrice (prices, discountTier) {
    if (!discountTier) {
      const discountedPrice = prices.reduce((accumulator, price) => {
        return accumulator + price
      }, 0)

      return discountedPrice
    }

    const discountedPrice = prices.reduce((accumulator, price) => {
      // Math.floor because Shopify rounds down
      const discountAmount = Math.floor(
        (price * discountTier.percentage) / 100,
      )
      const discountedPrice = price - discountAmount

      return accumulator + discountedPrice
    }, 0)

    return discountedPrice
  }

  updateSubmitButton () {
    if (!this.submitEls.length) return

    this.submitEls.forEach((el) => {
      const text =
        ProductSet.size === 0
          ? el.dataset.noItemsSelectedText
          : el.dataset.addToBagText.replace('[[ count ]]', ProductSet.size)

      el.textContent = text
      el.setAttribute('aria-disabled', ProductSet.size === 0)
    })
  }

  /**
   * @param {number} subTotalPrice
   * @param {number} discountedPrice
   * @param {string} formattedSubTotal
   * @param {string} formattedDiscountedPrice
   */
  updateStickySummaryTotals (
    subTotalPrice,
    discountedPrice,
    formattedSubTotal,
    formattedDiscountedPrice,
  ) {
    if (!this.summaryStickyEl) return

    this.summaryStickyEl.setAttribute(
      'total',
      discountedPrice > 0 ? formattedDiscountedPrice : '',
    )
    this.summaryStickyEl.setAttribute(
      'subtotal',
      subTotalPrice > discountedPrice ? formattedSubTotal : '',
    )
  }
}

customElements.define('product-set', ProductSet)

class ProductSetSummarySticky extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['content', 'total', 'subtotal', 'submit']
  }

  static get externalElements () {
    return ['content']
  }

  static get observedAttributes () {
    return ['total', 'subtotal']
  }

  constructor () {
    super()

    this.setupEls()
  }

  setupEls () {
    /** @type {HTMLElement} */
    this.contentEl = this.els?.content?.element
    /** @type {HTMLElement[]} */
    this.externalContentEls = this.externalEls?.content?.elements ?? []
    /** @type {HTMLElement} */
    this.totalEl = this.els?.total?.element
    /** @type {HTMLElement} */
    this.subTotalEl = this.els?.subtotal?.element
    /** @type {HTMLButtonElement} */
    this.submitEl = this.els?.submit?.element
  }

  setupListeners () {
    super.setupListeners()
    this.setupEls()

    this.listeners.add(
      window.BAO.eventBus,
      PRODUCT_SET_EVENTS.UPDATED,
      this.handleProductSetUpdate.bind(this),
    )

    if (this.submitEl) {
      this.listeners.add(
        this.submitEl,
        'click',
        this.handleStickySummaryClick.bind(this),
      )
    }
  }

  connectedCallback () {
    super.connectedCallback()

    this.reveal()
    this.handleProductSetUpdate()
  }

  reveal () {
    this.setAttribute('aria-hidden', 'false')
  }

  attributeChangedCallback (name, oldValue, newValue) {
    if (oldValue === newValue) return

    switch (name) {
      case 'total':
        this.updateElementContent(this.totalEl, newValue)
        break
      case 'subtotal':
        this.updateElementContent(this.subTotalEl, newValue)
        break
    }
  }

  /**
   * @param {HTMLElement} el
   * @param {string} value
   */
  updateElementContent (el, value) {
    if (!el) return

    el.textContent = value
    el.setAttribute('aria-hidden', Boolean(!value.length))
  }

  handleProductSetUpdate () {
    this.updateSummaryContent()
    this.updateSubmitButtonText()
  }

  updateSummaryContent () {
    if (!this.contentEl || !this.externalContentEls.length) return

    const externalContentEl = this.externalContentEls.find((el) =>
      el.closest('[data-product-set-el="group"][aria-hidden="false"]'),
    )
    if (!externalContentEl) return
    this.contentEl.innerHTML = externalContentEl.innerHTML
  }

  handleStickySummaryClick (event) {
    if (this.submitEl?.getAttribute('aria-disabled') === 'true') {
      event.preventDefault()
    }
  }

  updateSubmitButtonText () {
    if (!this.submitEl) return

    const text =
      ProductSet.size === 0
        ? this.submitEl.dataset.noItemsSelectedText
        : this.submitEl.dataset.addToBagText.replace(
          '[[ count ]]',
          ProductSet.size,
        )
    this.submitEl.textContent = text
    this.submitEl.setAttribute('aria-disabled', ProductSet.size === 0)
  }
}

customElements.define('product-set-summary-sticky', ProductSetSummarySticky)

class ProductSetProductGroups extends window.BAO.CustomElement() {
  setupEls () {
    /** @type {HTMLButtonElement[]} */
    this.triggerEls = this.els?.trigger?.elements ?? []
    /** @type {HTMLElement[]} */
    this.contentEls = this.els?.content?.elements ?? []
  }

  setupListeners () {
    super.setupListeners()
    this.setupEls()

    this.handleTriggerClick = this.handleTriggerClick.bind(this)

    this.triggerEls.forEach((el) => {
      this.listeners.add(el, 'click', this.handleTriggerClick)
    })
  }

  connectedCallback () {
    super.connectedCallback()

    this.setProductGroup(ProductSet.getGroup())
  }

  handleTriggerClick (event) {
    const triggerEl = event.target.closest(this.els?.trigger?.selector)
    const targetId = triggerEl?.getAttribute('aria-controls')

    if (!triggerEl) return
    if (triggerEl.getAttribute('aria-expanded') === 'true') return

    this.setProductGroup(targetId)
  }

  setProductGroup (targetId) {
    const contentEl = targetId
      ? this.contentEls.find((el) => el.id === targetId)
      : null
    if (!contentEl) return

    ProductSet.setGroup(targetId)

    this.triggerEls.forEach((el) =>
      el.setAttribute(
        'aria-expanded',
        el.getAttribute('aria-controls') === targetId,
      ),
    )
    this.contentEls.forEach((el) =>
      el.setAttribute('aria-hidden', el.id !== targetId),
    )
  }
}

customElements.define('product-set-product-groups', ProductSetProductGroups)

class ProductSetProduct extends window.BAO.CustomElement() {
  static get requiredElements () {
    return [
      'select',
      'add',
      'remove',
      'id',
      'quantity',
      'image-template',
      'thumbnail-template',
      'price-template',
    ]
  }

  static get requiredExternalElements () {
    return ['summary-product']
  }

  static get requiredAttributes () {
    return ['data-product-id', 'data-selected']
  }

  connectedCallback () {
    super.connectedCallback()

    if (!this.closest('product-set')) {
      console.error('product-set-product must be a child of product-set', this)
    }

    this.handleProductSetUpdate()
  }

  setupListeners () {
    super.setupListeners()
    this.setupEls()
    this.setupPostRenderListeners()

    this.listeners.add(
      window.BAO.eventBus,
      PRODUCT_SET_EVENTS.UPDATED,
      this.handleProductSetUpdate.bind(this),
    )

    this.listeners.add(
      window.BAO.eventBus,
      PRODUCT_SET_EVENTS.OPTIONS_RENDERED,
      this.handleOptionsRendered.bind(this),
    )

    this.listeners.add(document, 'click', this.handleDocumentClick.bind(this))
  }

  setupEls () {
    /** @type {HTMLSelectElement[]} */
    this.selectEls = this.els?.select?.selector
      ? getElements(this.els.select.selector, { context: this })
      : []

    /** @type {HTMLButtonElement[]} */
    this.addEls = this.els?.add?.selector
      ? getElements(this.els.add.selector, { context: this })
      : []

    /** @type {HTMLButtonElement[]} */
    this.removeEls = this.els?.remove?.selector
      ? getElements(this.els.remove.selector, { context: this })
      : []

    /** @type {HTMLTemplateElement} */
    this.imageTemplateEl = this.els?.['image-template']?.element
    /** @type {HTMLTemplateElement} */
    this.thumbnailTemplateEl = this.els?.['thumbnail-template']?.element
    /** @type {HTMLTemplateElement} */
    this.priceTemplateEl = this.els?.['price-template']?.element
    /** @type {HTMLElement[]} */
    this.summaryProductEls =
      this.externalEls?.['summary-product']?.elements ?? []

    /** @type {HTMLInputElement} */
    this.idEl = this.els?.id?.element
    /** @type {HTMLInputElement} */
    this.quantityEl = this.els?.quantity?.element
  }

  setupPostRenderListeners () {
    this.handleSelectChange = this.handleSelectChange.bind(this)

    this.selectEls.forEach((el) => {
      this.listeners.remove(el, 'change', this.handleSelectChange)
      this.listeners.add(el, 'change', this.handleSelectChange)
    })
  }

  handleOptionsRendered () {
    this.setupEls()
    this.setupPostRenderListeners()
    this.updatePrice()
    this.updateImage()
    this.updateButtonState()
    this.updateSummaryProductThumbnails()
  }

  handleDocumentClick (event) {
    const addElSelector = this.els?.add?.selector
    const removeElSelector = this.els?.remove?.selector
    const externalRemoveElSelector = this.externalEls?.remove?.selector

    const addEl = addElSelector && event.target.closest(addElSelector)
    const removeEl = removeElSelector && event.target.closest(removeElSelector)
    const externalRemoveEl =
      externalRemoveElSelector &&
      event.target.closest(externalRemoveElSelector)

    if (addEl) {
      this.handleAdd(addEl)
    }

    if (removeEl || externalRemoveEl) {
      this.handleRemove(removeEl || externalRemoveEl)
    }
  }

  updatePrice () {
    if (!this.priceTemplateEl) return

    const priceEl = getElement('[data-price-el]', { context: this })
    if (!priceEl) return

    priceEl.innerHTML = ''
    priceEl.appendChild(this.priceTemplateEl.content.cloneNode(true))
  }

  updateImage () {
    if (!this.imageTemplateEl) return

    const imageEl = getElement('[data-image-el]', { context: this })
    if (!imageEl) return

    imageEl.innerHTML = ''
    imageEl.appendChild(this.imageTemplateEl.content.cloneNode(true))
  }

  updateSummaryProductThumbnails () {
    if (!this.thumbnailTemplateEl || !this.summaryProductEls.length) return

    const summaryProductEl = this.summaryProductEls.find(
      (el) => el.dataset.productId === this.dataset.productId,
    )
    const thumbnailEl = getElement('[data-thumbnail-el]', {
      context: summaryProductEl,
    })

    if (!thumbnailEl) return

    thumbnailEl.innerHTML = ''
    thumbnailEl.appendChild(this.thumbnailTemplateEl.content.cloneNode(true))
  }

  handleSelectChange (event) {
    this.setSelectedVariant(
      event.target.options[event.target.selectedIndex]?.dataset?.variantId,
    )
  }

  setSelectedVariant (variantId) {
    if (!variantId) return

    this.dataset.variantId = variantId
    this.idEl.value = variantId
  }

  handleAdd (addEl) {
    if (!addEl || addEl?.getAttribute('aria-disabled') === 'true') return
    const productEl = getElements(this.identifier).find(
      (el) => el.dataset.productId === addEl.dataset.productId,
    )
    if (!productEl) return

    ProductSet.add(addEl.dataset.productId, productEl.dataset.variantId)
  }

  handleRemove (removeEl) {
    if (!removeEl || removeEl?.getAttribute('aria-disabled') === 'true') return

    ProductSet.delete(removeEl.dataset.productId)
  }

  handleProductSetUpdate () {
    this.setSelectedVariant(ProductSet.get(this.dataset.productId))
    this.toggleSelected()
    this.updateButtonState()
    this.updateSelectedQuantity()
    this.updateSummaryProductThumbnails()
  }

  toggleSelected () {
    this.setAttribute('data-selected', ProductSet.has(this.dataset.productId))
  }

  updateButtonState () {
    this.addEls.forEach((el) =>
      el.setAttribute(
        'aria-disabled',
        el.dataset.available === 'false' ||
          ProductSet.has(el.dataset.productId),
      ),
    )
    this.removeEls.forEach((el) =>
      el.setAttribute('aria-disabled', !ProductSet.has(el.dataset.productId)),
    )
  }

  updateSelectedQuantity () {
    this.quantityEl.value = ProductSet.has(this.dataset.productId) ? 1 : 0
  }
}

customElements.define('product-set-product', ProductSetProduct)

class ProductSetProductActions extends window.BAO.CustomElement() {
  static #fetchedFromUrl = new Set()

  #shopifySectionId = 'shopify-section-product-set-product-actions'
  #abortController = null

  static get requiredElements () {
    return ['select']
  }

  static get requiredAttributes () {
    return ['data-product-url', 'data-section-id', 'data-product-id']
  }

  connectedCallback () {
    super.connectedCallback()

    this.renderOptionsFromUrl()
    this.removeUnavailableProductsFromSet()
  }

  setupListeners () {
    super.setupListeners()
    this.setupEls()

    this.handleSelectChange = this.handleSelectChange.bind(this)

    this.listeners.add(
      window.BAO.eventBus,
      PRODUCT_SET_EVENTS.UPDATED,
      this.handleProductSetUpdate.bind(this),
    )

    this.selectEls.forEach((selectEl) => {
      this.listeners.add(selectEl, 'change', this.handleSelectChange)
    })
  }

  setupEls () {
    /** @type {HTMLSelectElement[]} */
    this.selectEls = this.els?.select?.elements ?? []
  }

  renderOptionsFromUrl () {
    if (ProductSetProductActions.#fetchedFromUrl.has(this.dataset.productId)) {
      return
    }
    ProductSetProductActions.#fetchedFromUrl.add(this.dataset.productId)

    const selectedVariantId = ProductSet.get(this.dataset.productId)

    if (!selectedVariantId) return

    this.renderOptions({ variantId: selectedVariantId })
  }

  removeUnavailableProductsFromSet () {
    if (this.dataset.available === 'true') return

    ProductSet.delete(this.dataset.productId)
  }

  handleProductSetUpdate () {
    const selectedVariantId = ProductSet.get(this.dataset.productId)

    if (!selectedVariantId) return

    this.selectEls.forEach((selectEl) => {
      const option = getElement(
        `option[data-variant-id="${selectedVariantId}"]`,
        { context: selectEl },
      )

      if (option) {
        selectEl.value = option.value
        selectEl.dispatchEvent(new Event('change'))
      }
    })
  }

  async handleSelectChange (event) {
    if (!event.isTrusted) return

    const variantId =
      event.target.options[event.target.selectedIndex]?.dataset?.variantId

    if (this.isBusy) {
      event.preventDefault()
      return
    }

    await this.renderOptions({
      variantId,
    })

    if (ProductSet.has(this.dataset.productId)) {
      ProductSet.add(this.dataset.productId, variantId)
    }

    getElement(`#${event.target.id}`, {
      context: document,
    })?.focus()
  }

  /**
   * Renders the options
   *
   * @param {Object} options
   * @param {string[]} [options.optionValues]
   * @param {string|null} [options.variantId]
   */
  async renderOptions ({ optionValues = [], variantId = null }) {
    const controller = new AbortController()
    const { productUrl, sectionId } = this.dataset
    if (!productUrl || !sectionId) return

    this.isBusy = true

    this.#abortController?.abort()
    this.#abortController = controller

    const sectionUrl = variantId
      ? `${productUrl}?section_id=${sectionId}&variant=${variantId}`
      : `${productUrl}?section_id=${sectionId}&option_values=${optionValues.join(',')}`
    const cacheKey = `product-options-${JSON.stringify({ optionValues, variantId })}`

    try {
      const cachedHtml = ProductSet.htmlOptionsCache.get(cacheKey)
      const htmlString =
        cachedHtml ??
        (await window.BAO.sectionRenderer.fetchSingle(sectionId, sectionUrl, {
          signal: controller.signal,
        }))
      const html = window.BAO.sectionRenderer.getOuterHtml(htmlString, {
        id: this.#shopifySectionId,
        selector: this.identifier,
      })

      ProductSet.htmlOptionsCache.set(cacheKey, htmlString)

      this.outerHTML = html
    } catch (error) {
      if (error.name === 'AbortError') return

      console.error(error)
    } finally {
      if (this.#abortController === controller) {
        this.#abortController = null
        this.isBusy = false

        window.BAO.dispatchEvent(PRODUCT_SET_EVENTS.OPTIONS_RENDERED)
      }
    }
  }

  get isBusy () {
    return this._isBusy ?? false
  }

  set isBusy (isBusy) {
    this._isBusy = isBusy
    this.setAttribute('aria-busy', isBusy)

    this.selectEls.forEach((selectEl) => {
      selectEl.setAttribute('aria-busy', isBusy)

      const preventDefault = (event) => event.preventDefault()

      if (isBusy) {
        this.listeners.add(selectEl, 'mousedown', preventDefault)
        this.listeners.add(selectEl, 'keydown', preventDefault)
      } else {
        this.listeners.remove(selectEl, 'mousedown', preventDefault)
        this.listeners.remove(selectEl, 'keydown', preventDefault)
      }
    })
  }
}

customElements.define('product-set-product-actions', ProductSetProductActions)
